import React from 'react';
import { Card, Row, Col } from 'antd';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import './blopsSchedule.scss';

import { getFinanceByStationMonth } from '../../modules/analytics';

// Local tier styles (match Finance components)
const tierStyles = {
  'super-peak': { border: '#E60F11', background: '#FFD6D6', label: 'Super Peak', labelSuffix: '尖' },
  'peak':       { border: '#FFA53C', background: '#FFE8CD', label: 'Peak',       labelSuffix: '峰' },
  'off-peak':   { border: '#50CD89', background: '#C4EBD9', label: 'Off Peak',   labelSuffix: '平' },
  'low':        { border: '#3CA8FF', background: '#CDE9FF', label: 'Low',        labelSuffix: '谷' },
  'bottom':     { border: '#AB4DCA', background: '#E69ADC', label: 'Bottom',     labelSuffix: '深谷' },
};

const BlopsSchedule = () => {
  const { inspectionTime } = useSelector(state => state.analytics || {});

  const dispatch = useDispatch();
  const stationId = inspectionTime?.stationInfo?.stationId || null;

  // Base month from inspectionTime (or today); safe to use before finance is selected
  const baseMonth = (inspectionTime?.date && moment(inspectionTime.date).format('YYYY-MM'))
    || moment().format('YYYY-MM');

  const monthKey = baseMonth;

  const finance = useSelector(state => {
    const byStation = (state.analytics?.financeByMonth || {})[stationId] || {};
    return byStation[monthKey] || null;
  });

  // Prefer API reportMonth for display; fetching key stays as baseMonth
  const monthLabel = finance?.reportMonth || monthKey;

  React.useEffect(() => {
    if (!stationId || !monthKey) return;
    if (!finance) {
      dispatch(getFinanceByStationMonth(stationId, monthKey));
    }
  }, [dispatch, stationId, monthKey, finance]);

  // === Dynamic data from monthly finance ===
  const TIER_KEY_BY_LETTER = { S: 'super-peak', P: 'peak', O: 'off-peak', L: 'low', B: 'bottom' };
  const LETTER_BY_TIER_KEY = { 'super-peak': 'S', 'peak': 'P', 'off-peak': 'O', 'low': 'L', 'bottom': 'B' };

  const site = finance?.site || {};
  const blops = Array.isArray(site?.blops) ? site.blops : [];
  const scheduleLetters = Array.isArray(site?.blopsSchedule) ? site.blopsSchedule : [];

  // Build price map per tier key from blops entries (fallback 0)
  const priceByTierKey = (() => {
    const map = { 'super-peak': 0, 'peak': 0, 'off-peak': 0, 'low': 0, 'bottom': 0 };
    blops.forEach(b => {
      const key = TIER_KEY_BY_LETTER[(b?.tier || '').toString().trim().toUpperCase()];
      if (key && typeof b?.unitPrice === 'number') {
        map[key] = b.unitPrice;
      }
    });
    return map;
  })();

  // TIER_VALUES array the rest of the component uses
  const TIER_VALUES = [
    { key: 'bottom',     price: priceByTierKey['bottom'] },
    { key: 'off-peak',   price: priceByTierKey['off-peak'] },
    { key: 'low',        price: priceByTierKey['low'] },
    { key: 'peak',       price: priceByTierKey['peak'] },
    { key: 'super-peak', price: priceByTierKey['super-peak'] },
  ];

  // Convert schedule letters (24 entries) to hourly price values
  const HOURLY_BINS_24 = (scheduleLetters.length === 24)
    ? scheduleLetters.map((ch) => priceByTierKey[TIER_KEY_BY_LETTER[(ch || '').toString().toUpperCase()]] || 0)
    : [];

  // Legend tiers derived from config
  const tiers = TIER_VALUES.map(t => ({
    key: t.key,
    border: tierStyles[t.key].border,
    background: tierStyles[t.key].background,
    label: `${tierStyles[t.key].label} ${tierStyles[t.key].labelSuffix ? `(${tierStyles[t.key].labelSuffix})` : ''}`,
    value:
      typeof t.price === 'number'
        ? (t.price === 0 ? '-' : `${t.price} ¥/kWh`)
        : '-'
  })).reverse();

  // ----- Chart helpers -----
  const CHART_HEIGHT = 200;
  const CHART_PADDING = { top: 12, right: 12, bottom: 12, left: 28 };
  const BASE_Y_STEP = 0.3;
  const MAX_TIER = TIER_VALUES.length ? Math.max(...TIER_VALUES.map(t => Number(t.price) || 0)) : 0;
  const MAX_DATA = HOURLY_BINS_24.length ? Math.max(...HOURLY_BINS_24.map(v => Number(v) || 0)) : 0;
  const Y_MAX_RAW = Math.max(MAX_TIER, MAX_DATA);
  const Y_MAX = Y_MAX_RAW > 0 ? Number(Y_MAX_RAW.toFixed(6)) : 1; // avoid zero max
  const yTicks = [];
  for (let v = 0; v < Y_MAX - 1e-9; v += BASE_Y_STEP) {
    yTicks.push(Number(v.toFixed(1)));
  }
  yTicks.push(Y_MAX);
  const xTicks = Array.from({ length: 12 }, (_, i) => i * 2); // 0..22 step 2 from a 24-hour series

  // compute dimensions dynamically (SVG is responsive via viewBox)
  const chartWidth = 350; // virtual canvas; will scale to container
  const chartHeight = CHART_HEIGHT;
  const innerW = chartWidth - CHART_PADDING.left - CHART_PADDING.right;
  const innerH = chartHeight - CHART_PADDING.top - CHART_PADDING.bottom;

  // xScale now supports a full 24-hour domain (0..24) so right edge is 24.
  const xScale = (h) => CHART_PADDING.left + (h / 24) * innerW;
  const yScale = (v) =>
    CHART_PADDING.top + innerH - (v / Y_MAX) * innerH;

  // === Auto-generate TOU blocks from hourly bins and tier thresholds ===
  const SORTED_TIERS = [...TIER_VALUES].sort((a, b) => a.price - b.price);
  const pickTier = (val) => {
    for (let i = 0; i < SORTED_TIERS.length; i++) {
      if (val <= SORTED_TIERS[i].price) return SORTED_TIERS[i].key;
    }
    return SORTED_TIERS[SORTED_TIERS.length - 1].key;
  };

  // Walk hours [0..23] and merge consecutive hours with same tier into blocks
  const rawBlocks = [];
  let curKey = null;
  let curStart = 0;
  for (let h = 0; h <= 23; h++) {
    const v = HOURLY_BINS_24[h] ?? 0;
    const key = pickTier(v);
    if (curKey == null) {
      curKey = key;
      curStart = h;
    } else if (key !== curKey) {
      rawBlocks.push({ key: curKey, start: curStart, end: h });
      curKey = key;
      curStart = h;
    }
    if (h === 23) rawBlocks.push({ key: curKey, start: curStart, end: 24 });
  }

  // Decorate blocks with styles/prices; label only first occurrence of each tier
  const seen = new Set();
  const TOU_BANDS = rawBlocks.map((b) => {
    const style = tierStyles[b.key] || {};
    const priceObj = TIER_VALUES.find(t => t.key === b.key);
    const price = priceObj ? priceObj.price : 0;
    const first = !seen.has(b.key);
    if (first) seen.add(b.key);
    return {
      key: b.key,
      start: b.start,
      end: b.end,
      price,
      fill: style.background,
      border: style.border,
      showLabel: first && price > 0,
    };
  });

  // (Not rendered) Example of mapping 24 bins to our 2h grid if needed later:
  const hourly2h = xTicks.map(h => HOURLY_BINS_24[h] ?? HOURLY_BINS_24[Math.min(h, 23)]);

  return (
    <Card className="blopsScheduleCard">
      <div className="scheduleHeader">
        <h3>Latest BLOPS Schedule</h3>
        <div className="month">{monthLabel}</div>
      </div>

      <div className="scheduleBox">
        <svg
          className="blopsChart"
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="BLOPS schedule chart"
        >
          {/* vertical TOU blocks (behind grid) */}
          {TOU_BANDS.map((b, i) => {
            const x = xScale(b.start);
            const w = xScale(b.end) - x;
            const y = yScale(b.price);
            const h = yScale(0) - y;
            return (
              <g key={`block-${i}`} className="tou-block">
                <line x1={x} y1={y} x2={x + w} y2={y} stroke={b.border} strokeWidth={2} />
                <rect x={x} y={y} width={w} height={h} fill={b.fill} />
                {b.showLabel ? (
                  <text x={x + w / 2} y={y - 6} className="block-label" textAnchor="middle">{b.price}</text>
                ) : null}
              </g>
            );
          })}

          {/* grid horizontal */}
          {yTicks.map((t, i) => {
            if (i === yTicks.length - 1) return null;
            return (
              <g key={`y-${t}`}>
                <line
                  x1={CHART_PADDING.left}
                  x2={CHART_PADDING.left + innerW}
                  y1={yScale(t)}
                  y2={yScale(t)}
                  className="grid-line"
                />
                <text x={CHART_PADDING.left - 6} y={yScale(t)} className="y-tick" dominantBaseline="middle" textAnchor="end">
                  {t.toFixed(1)}
                </text>
              </g>
            );
          })}

          {/* grid vertical + x labels */}
          {xTicks.map((t) => (
            <g key={`x-${t}`}>
              <line
                x1={xScale(t)}
                x2={xScale(t)}
                y1={CHART_PADDING.top}
                y2={CHART_PADDING.top + innerH}
                className="grid-line-vert"
              />
              <text x={xScale(t)} y={CHART_PADDING.top + innerH + 18} className="x-tick" textAnchor="middle">
                {t}
              </text>
            </g>
          ))}
        </svg>

        {/* Legend / price table */}
        <div className="legend">
          {tiers.map(t => (
            <Row key={t.key} className="legendRow" align="middle">
              <Col flex="0 0 24px">
                <span
                  className="swatch"
                  style={{
                    background: t.background,
                    border: `2px solid ${t.border}`
                  }}
                />
              </Col>
              <Col flex="auto">
                <span className="legendLabel">{t.label}</span>
              </Col>
              <Col flex="0 0 160px" className="legendValue">
                {t.value}
              </Col>
            </Row>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default BlopsSchedule;
